import { Loader, Select, TextInput, Pagination, Group, Text } from "@mantine/core";
import React, { useMemo } from "react";
import IconSearch from "../../../components/icons/icon-search";
import { Employee } from "../types";

interface EmployeeListPanelProps {
  employees: Employee[]; 
  totalItems: number; 
  isLoading: boolean;
  selectedEmployeeId: number | undefined;
  onSelectEmployee: (employee: Employee) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  departments: { value: string; label: string }[];
  selectedDepartment: string | null;
  onDepartmentChange: (value: string | null) => void;

  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (value: string) => void;
}

export const EmployeeListPanel: React.FC<EmployeeListPanelProps> = ({
  employees,
  totalItems, 
  isLoading,
  selectedEmployeeId,
  onSelectEmployee,
  searchTerm,
  onSearchChange,
  departments,
  selectedDepartment,
  onDepartmentChange,
  
  currentPage,
  totalPages,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  
  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={3} className="p-8 text-center">
            <Loader />
          </td>
        </tr>
      );
    }

    if (employees.length === 0) {
      return (
        <tr>
          <td colSpan={3} className="p-8 text-center text-slate-500">
            Không có dữ liệu
          </td>
        </tr>
      );
    }

    const startIndex = (currentPage - 1) * itemsPerPage; 

    return employees.map((emp, index) => (
      <tr 
        key={emp.id} 
        onClick={() => onSelectEmployee(emp)} 
        className={`cursor-pointer border-b hover:bg-green-50 ${selectedEmployeeId === emp.id ? "bg-green-100 font-semibold text-green-700" : ""}`}
      >
        <td className="w-12 px-4 py-3">{startIndex + index + 1}</td> 
        <td className="truncate px-4 py-3">{emp.name}</td>
        <td className="truncate px-4 py-3">{emp.department}</td>
      </tr>
    ));
  };

  return (
    <div className="flex w-1/3 min-w-[350px] max-w-[450px] flex-col overflow-hidden border-r border-slate-200 bg-white">
      <div className="border-b p-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-green-800">Danh sách Nhân viên</h2>
        <TextInput
          placeholder="Tìm kiếm nhân viên..."
          icon={<IconSearch />}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          className="mt-4"
        />
        <Select
          placeholder="Lọc theo bộ phận"
          data={departments}
          value={selectedDepartment}
          onChange={onDepartmentChange}
          className="mt-2"
          clearable
        />
      </div>

      <div className="h-0 flex-1 overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 z-10 border-b bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="w-12 px-4 py-2">STT</th>
              <th className="px-4 py-2">Tên Nhân Viên</th>
              <th className="px-4 py-2">Bộ Phận</th>
            </tr>
          </thead>
          <tbody>
            {renderTableBody()}
          </tbody>
        </table>
      </div>
      
      {/* --- PHÂN TRANG (Pagination Footer) --- */}
      <div className="border-t p-4">
        <Group position="apart">
          <Group spacing="xs">
            <Select
              data={['10', '20', '50', '100']}
              value={String(itemsPerPage)}
              onChange={(value) => onItemsPerPageChange(value || '10')}
              size="xs"
              className="w-[80px]"
            />
            {/* Hiển thị tổng số mục */}
            <Text size="sm" color="dimmed">
              trong tổng số {totalItems}
            </Text>
          </Group>

          {/* Component phân trang */}
          <Pagination
            total={totalPages}
            page={currentPage}
            onChange={onPageChange}
            size="sm"
            withControls={false}
          />
        </Group>
      </div>
    </div>
  );
};
